import { AppBarSubmenuItem, StyledAppBarContainer, AppBarSubmenu, AppBarSubmenuTheme } from "@iventis/components";
import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch, useLocation, Redirect, useRouteMatch } from "react-router-dom";
import { listItemToCreateDto } from "@iventis/utilities";
import { Content } from "@iventis/translations/content/typed-content";
import { ApplicationName } from "@iventis/domain-model/model/applicationName";
import { IconName, IconPrefix } from "@fortawesome/fontawesome-svg-core";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { authSlice } from "@iventis/permissions/src/auth.slice";
import { mappingApi } from "@iventis/api/src/api";
import { aggregatePagedResponseLists } from "@iventis/api-helpers";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { PagedResult } from "@iventis/domain-model/model/pagedResult";
import { exportDataFieldListItems, importDataFieldListItems, updateLayerDataFieldListItemOrder } from "@iventis/plans/src/services/data-field-list-item-service";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { toast } from "@iventis/toasts/src/toast";
import { ProjectSettingsSubmenuOptions } from "@iventis/project-settings/src/project-settings.types";
import {
    billingRoute,
    projectAttributesRoute,
    projectCustomisationRoute,
    projectRolePermissionsRoute,
    projectScheduleConfigRoute,
    sitemapsRoute,
    ssoRoute,
} from "@iventis/project-settings/src/constants";
import { ProjectRolePermissions } from "@iventis/project-settings/src/project-role-permissions";
import { ProjectCustomisation } from "@iventis/project-settings/src/project-customisation";
import { SitemapSettingsPage } from "@iventis/project-settings/src/sitemap-settings-page";
import { ProjectBilling } from "@iventis/project-settings/src/project-billing";
import { ProjectScheduleConfig } from "@iventis/schedule/src/project-schedule-config";
import { editStyleService } from "@iventis/plans/src/services/edit-style-service";
import { RootState } from "@iventis/plans/src/state/root.store";
import { AppSubBarMenuContext } from "@iventis/utilities/src/app-bar-submenu-context";
import { IventisRoute as Route } from "@iventis/permissions/src/iventis-route";
import { LIST_ITEMS_QUERY_KEY, ProjectAttributes } from "@iventis/datafield-editor";
import { useHistory } from "@iventis/subscriptions";
import { useQueryClient } from "@tanstack/react-query";
import { ExportType, createListItemExportNotification, notificationNameCreator, useNotificationContext } from "@iventis/notifications";
import { v4 as uuid } from "uuid";
import { ProjectSsoConfig } from "./project-sso-config";

export const ProjectSettingsPage: React.FC<PropsWithChildren<{}>> = ({ children }) => {
    const dispatch = useDispatch();
    const { path } = useRouteMatch();

    const history = useHistory();
    const { pathname } = useLocation();

    const translate = useIventisTranslate();

    const queryClient = useQueryClient();

    const {
        projectApplications,
        user: { userId, projectId },
    } = useSelector((state: RootState) => state.auth);

    const scheduleApplicationPresent = useMemo(() => projectApplications.some((a) => a.name === ApplicationName.Schedule), [projectApplications]);
    const mappingApplicationPresent = useMemo(() => projectApplications.some((a) => a.name === ApplicationName.Maps), [projectApplications]);

    const notifications = useNotificationContext();

    const appSubmenuOptions = useMemo(
        () => [
            {
                id: ProjectSettingsSubmenuOptions.Customisation,
                name: "Project Customisation",
                pageIcon: { prefix: "fas" as IconPrefix, iconName: "pencil" as IconName },
                sidePanelIcon: { prefix: "fas" as IconPrefix, iconName: "pencil" as IconName },
            },
            {
                id: ProjectSettingsSubmenuOptions.ProjectRolePermissions,
                name: translate(Content.settings3.projectRolePermissions.projectAdministratorsSubMenu),
                pageIcon: { prefix: "far" as IconPrefix, iconName: "users" as IconName },
                sidePanelIcon: { prefix: "far" as IconPrefix, iconName: "users" as IconName },
            },
            {
                id: ProjectSettingsSubmenuOptions.Attributes,
                name: translate(Content.settings4.projectAttributes.title),
                pageIcon: { prefix: "fas" as IconPrefix, iconName: "tag" as IconName },
                sidePanelIcon: { prefix: "fas" as IconPrefix, iconName: "tag" as IconName },
            },
            ...(mappingApplicationPresent
                ? [
                      {
                          id: ProjectSettingsSubmenuOptions.Sitemaps,
                          name: translate(Content.settings3.sitemaps.title),
                          pageIcon: { prefix: "far" as IconPrefix, iconName: "map" as IconName },
                          sidePanelIcon: { prefix: "far" as IconPrefix, iconName: "map" as IconName },
                      },
                  ]
                : []),
            ...(scheduleApplicationPresent
                ? [
                      {
                          id: ProjectSettingsSubmenuOptions.ScheduleConfig,
                          name: translate(Content.settings.scheduleConfig.title),
                          pageIcon: { prefix: "far" as IconPrefix, iconName: "calendar" as IconName },
                          sidePanelIcon: { prefix: "far" as IconPrefix, iconName: "calendar" as IconName },
                      },
                  ]
                : []),
            {
                id: ProjectSettingsSubmenuOptions.Billing,
                name: translate(Content.settings2.billing.title),
                pageIcon: { prefix: "fas" as IconPrefix, iconName: "file-invoice" as IconName },
                sidePanelIcon: { prefix: "fas" as IconPrefix, iconName: "file-invoice" as IconName },
            },
            {
                id: ProjectSettingsSubmenuOptions.Sso,
                name: translate(Content.settings3.sso.title),
                pageIcon: { prefix: "fas" as IconPrefix, iconName: "key" as IconName },
                sidePanelIcon: { prefix: "fas" as IconPrefix, iconName: "key" as IconName },
            },
        ],
        [translate]
    );

    useEffect(() => {
        switch (pathname) {
            case projectCustomisationRoute:
                setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.Customisation);
                break;
            case projectRolePermissionsRoute:
                setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.ProjectRolePermissions);
                break;
            case projectScheduleConfigRoute:
                if (scheduleApplicationPresent) {
                    setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.ScheduleConfig);
                }
                break;
            case projectAttributesRoute:
                setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.Attributes);
                break;
            case sitemapsRoute:
                setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.Sitemaps);
                break;
            case billingRoute:
                setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.Billing);
                break;
            case ssoRoute:
                setSelectedSubmenuOption(ProjectSettingsSubmenuOptions.Sso);
                break;
            default:
                break;
        }
    }, [pathname]);

    const [selectedSubmenuOption, setSelectedSubmenuOption] = useState<ProjectSettingsSubmenuOptions>(appSubmenuOptions[0].id);

    const { mobileDrawerOpen, setMobileDrawerOpen } = useContext(AppSubBarMenuContext);

    const submenuItemSelected = (item: AppBarSubmenuItem) => {
        setMobileDrawerOpen(false);

        if (item.id === ProjectSettingsSubmenuOptions.Customisation) {
            history.push(projectCustomisationRoute);
        }
        if (item.id === ProjectSettingsSubmenuOptions.ProjectRolePermissions) {
            history.push(projectRolePermissionsRoute);
        }
        if (item.id === ProjectSettingsSubmenuOptions.Attributes) {
            history.push(projectAttributesRoute);
        }
        if (mappingApplicationPresent && item.id === ProjectSettingsSubmenuOptions.Sitemaps) {
            history.push(sitemapsRoute);
        }
        if (scheduleApplicationPresent && item.id === ProjectSettingsSubmenuOptions.ScheduleConfig) {
            history.push(projectScheduleConfigRoute);
        }
        if (item.id === ProjectSettingsSubmenuOptions.Billing) {
            history.push(billingRoute);
        }
        if (item.id === ProjectSettingsSubmenuOptions.Sso) {
            history.push(ssoRoute);
        }
    };

    return (
        <StyledAppBarContainer>
            <IventisErrorBoundaryWithToast toast={toast}>
                <AppBarSubmenu
                    theme={AppBarSubmenuTheme.Administrative}
                    items={appSubmenuOptions}
                    mobileOpen={mobileDrawerOpen}
                    fullScreenTitle="Project Settings"
                    selectedItem={selectedSubmenuOption}
                    onItemClick={submenuItemSelected}
                />
                <Switch>
                    {/* Remove trailing slashes */}
                    <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />
                    <Route exact path={`${path}/project-customisation`} component={ProjectCustomisation} />
                    <Route exact path={`${path}/project-role-permissions`} render={() => <ProjectRolePermissions projectId={projectId} userId={userId} />} />
                    <Route
                        exact
                        path={`${path}/project-attributes`}
                        render={() => (
                            <ProjectAttributes
                                projectId={projectId}
                                projectAttributeApiFunctions={{
                                    get: editStyleService.getProjectDataFields,
                                    post: editStyleService.postProjectAttribute,
                                    patch: editStyleService.patchProjectAttribute,
                                    delete: (dataFieldId: string) => mappingApi.delete(`projects/${projectId}/data-fields/${dataFieldId}`),
                                    isDataFieldBeingUsed: (dataFieldId: string, resourceId: string) =>
                                        mappingApi
                                            .get<boolean>(`projects/${resourceId}/data-fields/${dataFieldId}/usage`, { suppressToasts: true })
                                            .then((res) => res.data),
                                }}
                                listItemService={{
                                    getDataFieldListItems: (dataFieldId) =>
                                        aggregatePagedResponseLists(async () =>
                                            mappingApi.get<PagedResult<DataFieldListItem[]>>(`data-fields/${dataFieldId}/list-items/filter`).then((res) => res.data)
                                        ),
                                    getDataFieldListItem: (dataFieldId, listItemId) =>
                                        mappingApi.get<DataFieldListItem>(`data-fields/${dataFieldId}/list-items/${listItemId}`).then((res) => res.data),
                                    postDataFieldListItem: async (listItem, dataFieldId) => {
                                        await mappingApi.post<DataFieldListItem>(`data-fields/${dataFieldId}/list-items`, [listItemToCreateDto(listItem)], {
                                            customErrorMessage: {
                                                title: translate(Content.errors.custom_network_error.create.title, { entity: translate(Content.attributes.attribute_list_item) }),
                                                message: translate(Content.errors.custom_network_error.create.message, {
                                                    entity: translate(Content.attributes.attribute_list_item),
                                                }),
                                            },
                                        });
                                    },
                                    putDataFieldListItem: async (listItem, dataFieldId) => {
                                        await mappingApi.patch<DataFieldListItem>(`data-fields/${dataFieldId}/list-items/${listItem.id}`, listItem, {
                                            customErrorMessage: {
                                                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.attributes.attribute_list_item) }),
                                                message: translate(Content.errors.custom_network_error.update.message, {
                                                    entity: translate(Content.attributes.attribute_list_item),
                                                }),
                                            },
                                        });
                                        queryClient.removeQueries([LIST_ITEMS_QUERY_KEY, dataFieldId]);
                                    },
                                    patchDataFieldListItemOrder: updateLayerDataFieldListItemOrder,
                                    exportDataFieldListItems: async (dataFieldId, dataFieldName) => {
                                        const notificationId = uuid();
                                        const notificationName = notificationNameCreator(ExportType.LayerAttribute, dataFieldName);
                                        const notification = createListItemExportNotification(userId, projectId, notificationId, notificationName);
                                        notifications.createNotification(notification);
                                        exportDataFieldListItems({ dataFieldId }, notificationId);
                                    },
                                    importDataFieldListItems: async (dataFieldId, csv, projectId, deleteExisting) => importDataFieldListItems({ dataFieldId, csv, deleteExisting }),
                                    deleteDataFieldListItem: async (listItemId, dataFieldId) => mappingApi.delete(`data-fields/${dataFieldId}/list-items?ids=${listItemId}`),
                                    isListItemBeingUsed: (dataFieldId: string, listItemId: string) =>
                                        mappingApi
                                            .get<boolean>(`data-fields/${dataFieldId}/list-items/${listItemId}/usage`, { suppressToasts: true })
                                            .then((res) => res.data),
                                }}
                                toast={toast}
                            />
                        )}
                    />
                    {mappingApplicationPresent && <Route exact path={`${path}/sitemaps`} render={() => <SitemapSettingsPage />} />}
                    {scheduleApplicationPresent && (
                        <Route
                            exact
                            path={`${path}/project-schedule-config`}
                            render={() => (
                                <ProjectScheduleConfig
                                    title={translate(Content.settings.scheduleConfig.title)}
                                    description={translate(Content.settings.scheduleConfig.description)}
                                    // Tell the auth slice that the schedule is updating so it can block navigation to it and display the loading spinner
                                    onSaving={() => dispatch(authSlice.actions.setApplicationUpdating({ isUpdating: true, application: ApplicationName.Schedule }))}
                                    onSaved={() =>
                                        // After the schedule has finished saving, wait 5 seconds before telling the auth slice it's finished, to allow for kafka to make updates in the backend
                                        setTimeout(() => {
                                            dispatch(authSlice.actions.setApplicationUpdating({ isUpdating: false, application: ApplicationName.Schedule }));
                                        }, 5000)
                                    }
                                />
                            )}
                        />
                    )}
                    <Route exact path={path} component={ProjectCustomisation} />
                    <Route exact path={`${path}/billing`} render={() => <ProjectBilling projectId={projectId} />} />
                    <Route exact path={`${path}/sso-config`} render={() => <ProjectSsoConfig projectId={projectId} />} />
                </Switch>
            </IventisErrorBoundaryWithToast>
            {children}
        </StyledAppBarContainer>
    );
};
